import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Box,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetProjectsQuery } from "../../projects/api/projectsApi";
import { useAddUnitMutation, useUpdateUnitMutation } from "../api/unitApi";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  unit?: any | null;
}

const AddEditUnit: React.FC<Props> = ({ open, setModalOpen, onSuccess, unit }) => {
  const { data: projects } = useGetProjectsQuery();
  const [createUnit] = useAddUnitMutation();
  const [updateUnit] = useUpdateUnitMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const initialFormState = {
    unit_code: "",
    unit_image: null as File | null,
    existingUnitImage: null as string | null,
    images: [] as File[],
    existingImages: [] as string[],
    project_id: null as number | null,
    unit_type_id: null as number | null,
    price_min: "",
    price_max: "",
    size_sqm: "",
    availability: "Available",
    bedrooms: 0,
    bathrooms: 0,
    special_features: [] as string[],
    description: "",
    is_featured: false,
  };

  const [formData, setFormData] = useState(initialFormState);

  const resetForm = () => {
    setFormData(initialFormState);
    setSubmissionError(null);
    setLoading(false);
  };

  useEffect(() => {
    if (unit) {
      setFormData({
        unit_code: unit.unit_code,
        unit_image: null,
        existingUnitImage: unit.image || null,
        images: [],
        existingImages: unit.images || [],
        project_id: unit.project?.id || null,
        unit_type_id: unit.unit_type?.id || null,
        price_min: unit.price_min,
        price_max: unit.price_max,
        size_sqm: unit.size_sqm,
        availability: unit.availability,
        bedrooms: unit.bedrooms,
        bathrooms: unit.bathrooms,
        special_features: unit.special_features || [],
        description: unit.description,
        is_featured: unit.is_featured,
      });
    } else {
      resetForm();
    }
  }, [unit]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'main' | 'list') => {
    const files = Array.from(e.target.files || []);
    if (type === 'main' && files[0]) {
      setFormData(prev => ({ ...prev, unit_image: files[0] }));
    } else if (type === 'list') {
      setFormData(prev => ({ ...prev, images: [...prev.images, ...files] }));
    }
  };

  const removeMainImage = () => {
    setFormData(prev => ({ ...prev, unit_image: null, existingUnitImage: null }));
  };

  const removeAdditionalImage = (index: number) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      newImages.splice(index, 1);
      return { ...prev, images: newImages };
    });
  };

  const removeExistingImage = (index: number) => {
    setFormData(prev => {
      const newImages = [...prev.existingImages];
      newImages.splice(index, 1);
      return { ...prev, existingImages: newImages };
    });
  };

  const handleSubmit = async () => {
    const fd = new FormData();
    fd.append("unit_code", formData.unit_code);
    if (formData.unit_image) fd.append("unit_image", formData.unit_image);
    if (formData.existingUnitImage === null) fd.append("remove_unit_image", "1");
    formData.images.forEach((file, i) => fd.append(`images[${i}]`, file));
    if (formData.project_id) fd.append("project_id", formData.project_id.toString());
    if (formData.unit_type_id) fd.append("unit_type_id", formData.unit_type_id.toString());
    fd.append("price_min", formData.price_min);
    fd.append("price_max", formData.price_max);
    fd.append("size_sqm", formData.size_sqm);
    fd.append("availability", formData.availability);
    fd.append("bedrooms", formData.bedrooms.toString());
    fd.append("bathrooms", formData.bathrooms.toString());
    formData.special_features.forEach((feature, i) => fd.append(`special_features[${i}]`, feature));
    fd.append("description", formData.description);
    fd.append("featured", formData.is_featured ? "1" : "0");

    try {
      setLoading(true);
      if (unit?.id) {
        await updateUnit({ id: unit.id, body: fd }).unwrap();
      } else {
        await createUnit({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      setModalOpen(false);
      console.error("Error saving unit:", err);
      setSubmissionError("Failed to save unit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedProject = projects?.data.find((p:any) => p.id === formData.project_id);
  const availableUnitTypes = selectedProject?.unit_types || [];

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        setModalOpen(false);
        resetForm();
      }} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>{unit ? "Edit Unit" : "Add Unit"}</DialogTitle>
      <DialogContent>
        <TextField fullWidth label="Unit Code" margin="dense" value={formData.unit_code} onChange={e => setFormData({ ...formData, unit_code: e.target.value })} />

        <TextField select fullWidth label="Project" margin="dense" value={formData.project_id ?? ""} onChange={e => setFormData({ ...formData, project_id: +e.target.value, unit_type_id: null })}>
          {projects?.data.map((p:any) => (
            <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
          ))}
        </TextField>

        <TextField select fullWidth label="Unit Type" margin="dense" value={formData.unit_type_id ?? ""} onChange={e => setFormData({ ...formData, unit_type_id: +e.target.value })}>
          {availableUnitTypes.map((ut:any) => (
            <MenuItem key={ut.id} value={ut.id}>{ut.name}</MenuItem>
          ))}
        </TextField>

        <TextField fullWidth label="Min Price" type="number" margin="dense" value={formData.price_min} onChange={e => setFormData({ ...formData, price_min: e.target.value })} />
        <TextField fullWidth label="Max Price" type="number" margin="dense" value={formData.price_max} onChange={e => setFormData({ ...formData, price_max: e.target.value })} />
        <TextField fullWidth label="Size (sqm)" type="number" margin="dense" value={formData.size_sqm} onChange={e => setFormData({ ...formData, size_sqm: e.target.value })} />

        <TextField select fullWidth label="Availability" margin="dense" value={formData.availability} onChange={e => setFormData({ ...formData, availability: e.target.value })}>
          {["Available", "Sold", "Reserved"].map(status => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </TextField>

        <TextField fullWidth label="Bedrooms" type="number" margin="dense" value={formData.bedrooms} onChange={e => setFormData({ ...formData, bedrooms: +e.target.value })} />
        <TextField fullWidth label="Bathrooms" type="number" margin="dense" value={formData.bathrooms} onChange={e => setFormData({ ...formData, bathrooms: +e.target.value })} />

        <TextField fullWidth label="Special Features (comma separated)" margin="dense" value={formData.special_features.join(", ")} onChange={e => setFormData({ ...formData, special_features: e.target.value.split(",").map(s => s.trim()) })} />
        <TextField fullWidth label="Description" margin="dense" multiline rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
            />
          }
          label="Featured Unit"
        />

        <Box display="flex" alignItems="center" gap={2} mt={2}>
          <Button variant="outlined" component="label">Upload Unit Image<input hidden accept="image/*" type="file" onChange={(e) => handleFileChange(e, 'main')} /></Button>
        </Box>
        {formData.unit_image && (
          <Box position="relative" display="inline-block" mt={2}>
            <img src={URL.createObjectURL(formData.unit_image)} alt="preview" width={100} height={100} />
            <IconButton onClick={removeMainImage} size="small" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: "white" }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        {formData.existingUnitImage && !formData.unit_image && (
          <Box position="relative" display="inline-block" mt={2}>
            <img src={formData.existingUnitImage} alt="existing unit" width={100} height={100} />
            <IconButton onClick={removeMainImage} size="small" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: "white" }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Box display="flex" alignItems="center" gap={2} mt={2}>
          <Button variant="outlined" component="label">Upload Additional Images<input hidden multiple accept="image/*" type="file" onChange={(e) => handleFileChange(e, 'list')} /></Button>
        </Box>

        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
          {formData.images.map((img, idx) => (
            <Box key={idx} position="relative">
              <img src={URL.createObjectURL(img)} alt="unit" width={100} height={100} />
              <IconButton onClick={() => removeAdditionalImage(idx)} size="small" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: "white" }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {formData.existingImages.map((url, idx) => (
            <Box key={idx} position="relative">
              <img src={url} alt="unit" width={100} height={100} />
              <IconButton onClick={() => removeExistingImage(idx)} size="small" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: "white" }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        {submissionError && <Alert severity="error" sx={{ mt: 2 }}>{submissionError}</Alert>}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setModalOpen(false)}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : (unit ? "Update" : "Add Unit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditUnit;
